import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import AdminDashboard from './AdminDashboard';
import FirebaseAdminLogin from './FirebaseAdminLogin';
import { useAuthenticationOptimized } from '../hooks/useAuthenticationOptimized';
import AuthLogger from '../utils/authLogger';

const FirebaseAdminPage: React.FC = () => {
    const {
        user,
        isLoading,
        isAuthenticating,
        signIn,
        signOut,
        isAuthenticated,
        isAdmin,
        getSecurityAlerts: _getSecurityAlerts
    } = useAuthenticationOptimized({
        autoRefresh: true,
        refreshInterval: 60000,
        enableRealTime: true,
        enableMFAFeature: true,
        enableRiskScoring: true,
        enableSessionManagement: true,
        enableAuditLogging: true,
        sessionTimeout: 30,
        maxSessions: 5
    });

    const [isInitialized, setIsInitialized] = useState(false);
    const [timeoutReached, setTimeoutReached] = useState(false);

    useEffect(() => {
        AuthLogger.info('FirebaseAdminPage', 'Component initialized', {
            isAuthenticated: isAuthenticated(),
            user,
            isLoading,
            loadingMessage: 'Loading Firebase Authentication...'
        });

        // Set a timeout to prevent infinite loading
        const timeout = setTimeout(() => {
            if (!isInitialized) {
                AuthLogger.warn('FirebaseAdminPage', 'Firebase auth timeout - showing login form');
                setTimeoutReached(true);
            }
        }, 5000);

        return () => clearTimeout(timeout);
    }, [isInitialized, isAuthenticated, user, isLoading]);

    useEffect(() => {
        if (user && isAuthenticated() && isAdmin()) {
            setIsInitialized(true);
            AuthLogger.info('FirebaseAdminPage', 'User authenticated as admin', {
                userId: user.uid,
                email: user.email
            });
        }
    }, [user, isAuthenticated, isAdmin]);

    const handleLogout = useCallback(async () => {
        try {
            AuthLogger.info('FirebaseAdminPage', 'Logging out user');
            await signOut();
            setIsInitialized(false);
            setTimeoutReached(false);
        } catch (error) {
            AuthLogger.error('FirebaseAdminPage', 'Error during logout', { error });
        }
    }, [signOut]);

    if (isLoading && !timeoutReached) {
        AuthLogger.info('FirebaseAdminPage', 'Rendering loading screen', {
            isLoading,
            loadingMessage: 'Loading Firebase Authentication...'
        });
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6">Loading Firebase Authentication...</Typography>
                <Typography variant="body2" color="text.secondary">This may take a few seconds...</Typography>
                <Button
                    variant="outlined"
                    onClick={() => setTimeoutReached(true)}
                    sx={{ mt: 2 }}
                >
                    Skip Loading & Show Login
                </Button>
            </Box>
        );
    }

    if (!isAuthenticated() || !isAdmin()) {
        AuthLogger.info('FirebaseAdminPage', 'Rendering login form', {
            isAuthenticated: isAuthenticated(),
            hasUser: !!user,
            isAdmin: isAdmin()
        });
        console.log('🎯 RENDERING LOGIN FORM - This should be visible now!');
        return <FirebaseAdminLogin onSignIn={signIn} isAuthenticating={isAuthenticating} />;
    }

    return <AdminDashboard sessionId={user?.uid || 'admin'} onLogout={handleLogout} />;
};

export default FirebaseAdminPage;